import { useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from '../../eventBus';
import HighlightedButton from './HighlightedButton';
import { AccountType } from '../../services/utils/types';
import { Auth } from 'aws-amplify';
import { ensurePushSubscription } from '../../services/pushNotifications';

const MobileMenu = ({ isDrawerOpen, toggleDrawer, menuItems, pathname }) => {
  const {
    signOut,
    currentPlan,
    conversations,
    accountDetails,
    type,
    isDualUser,
    authenticate,
    isImpersonated,
  } = useAccount();
  const isSafari = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
  }, []);

  const closeMenu = () => {
    toggleDrawer(false);
  };

  const handleLogout = () => {
    if (typeof signOut === 'function') {
      signOut();
      closeMenu();
    } else {
      console.log('signOut function is not provided');
    }
  };

  const [switchAccountLoading, setSwitchAccountLoading] = useState(false);

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={closeMenu}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
        },
        '& .MuiDrawer-paper': {
          width: '100%',
          maxWidth: '400px',
          marginTop: currentPlan.specialOffersUrl ? '120px' : '60px',
          padding: '16px 20px',
          backgroundColor: '#f9fbff',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{ fontSize: '32px', fontWeight: '600', mb: 1, padding: 0 }}
        >
          Menu
        </Typography>
        <List sx={{ p: 0 }}>
          {type === AccountType.ADVISOR && (
            <ListItem
              sx={{
                p: 0,
                '&:not(:last-child)': { mb: 1 },
              }}
            >
              <Button
                onClick={() => {
                  closeMenu();
                  eventBus.dispatch(eventBusValues.triggerAdvisorDrawerAction, {
                    ...accountDetails,
                    openInEdit: false,
                  });
                }}
                sx={{
                  display: 'block',
                  textTransform: 'none',
                  textDecoration: 'none',
                  color: '#000',
                  fontWeight: 'normal',
                  padding: 0,
                  textAlign: 'left',
                }}
              >
                <ListItemText
                  primary="View Profile"
                  primaryTypographyProps={{
                    sx: { fontSize: '22px', lineHeight: '1.1' },
                  }}
                />
              </Button>
            </ListItem>
          )}
          {type === AccountType.ADVISOR && (
            <ListItem
              sx={{
                p: 0,
                '&:not(:last-child)': { mb: 1 },
              }}
            >
              <Button
                onClick={() => {
                  closeMenu();
                  eventBus.dispatch(eventBusValues.keyDatesAction, {
                    action: 'open',
                  });
                }}
                sx={{
                  display: 'block',
                  textTransform: 'none',
                  textDecoration: 'none',
                  color: '#000',
                  fontWeight: 'normal',
                  padding: 0,
                  textAlign: 'left',
                }}
              >
                <ListItemText
                  primary="Key Dates"
                  primaryTypographyProps={{
                    sx: { fontSize: '22px', lineHeight: '1.1' },
                  }}
                />
              </Button>
            </ListItem>
          )}
          {type === AccountType.ADVISOR && (
            <ListItem
              sx={{
                p: 0,
                '&:not(:last-child)': { mb: 1 },
              }}
            >
              <Button
                onClick={() => {
                  closeMenu();
                  eventBus.dispatch('openProfessionalGoals');
                }}
                sx={{
                  display: 'block',
                  textTransform: 'none',
                  textDecoration: 'none',
                  color: '#000',
                  fontWeight: 'normal',
                  padding: 0,
                  textAlign: 'left',
                }}
              >
                <ListItemText
                  primary="My Professional Goals"
                  primaryTypographyProps={{
                    sx: { fontSize: '22px', lineHeight: '1.1' },
                  }}
                />
              </Button>
            </ListItem>
          )}
          {conversations && conversations.length > 0 && (
            <ListItem
              sx={{
                p: 0,
                mb: 1,
              }}
            >
              <Button
                onClick={() => {
                  closeMenu();
                  eventBus.dispatch(
                    eventBusValues.triggerConversationDrawerV2,
                    {},
                  );
                }}
                sx={{
                  display: 'block',
                  textTransform: 'none',
                  textDecoration: 'none',
                  color: '#000',
                  fontWeight: 'normal',
                  padding: 0,
                  textAlign: 'left',
                  minWidth: 0,
                }}
              >
                <ListItemText
                  primary="Messages"
                  primaryTypographyProps={{
                    sx: { fontSize: '22px', lineHeight: '1.1' },
                  }}
                />
              </Button>
            </ListItem>
          )}

          {isDualUser && !isImpersonated && (
            <ListItem
              sx={{
                p: 0,
                mb: 1,
              }}
            >
              <Button
                onClick={async () => {
                  setSwitchAccountLoading(true);
                  const user = await Auth.currentAuthenticatedUser();
                  await Auth.updateUserAttributes(user, {
                    profile:
                      AccountType.COMPANY === type
                        ? AccountType.ADVISOR
                        : AccountType.COMPANY,
                  });
                  await authenticate();
                }}
                sx={{
                  display: 'block',
                  textTransform: 'none',
                  textDecoration: 'none',
                  color: '#000',
                  fontWeight: 'normal',
                  padding: 0,
                  textAlign: 'left',
                  minWidth: 0,
                }}
              >
                <ListItemText
                  primary={
                    switchAccountLoading
                      ? 'Switching...'
                      : `Switch to ${
                          AccountType.COMPANY === type ? 'Advisor' : 'Company'
                        }`
                  }
                  primaryTypographyProps={{
                    sx: { fontSize: '22px', lineHeight: '1.1' },
                  }}
                />
              </Button>
            </ListItem>
          )}
          <ListItem
            sx={{
              p: 0,
              mb: 1,
            }}
          >
            <Button
              onClick={() => {
                closeMenu();
                AccountType.ADVISOR === type
                  ? eventBus.dispatch(eventBusValues.advisorAccountSettings, {})
                  : eventBus.dispatch(
                      eventBusValues.companyAccountSettings,
                      {},
                    );
              }}
              sx={{
                display: 'block',
                textTransform: 'none',
                textDecoration: 'none',
                color: '#000',
                fontWeight: 'normal',
                padding: 0,
                textAlign: 'left',
                minWidth: 0,
              }}
            >
              <ListItemText
                primary="Membership Settings"
                primaryTypographyProps={{
                  sx: { fontSize: '22px', lineHeight: '1.1' },
                }}
              />
            </Button>
          </ListItem>
          {type === AccountType.ADVISOR && (
            <ListItem
              sx={{
                p: 0,
                mb: 1,
              }}
            >
              <HighlightedButton />
            </ListItem>
          )}
          {/* {isSafari && (
            <ListItem
              sx={{
                p: 0,
                mb: 1,
              }}
            >
              <Button
                onClick={async () => {
                  try {
                    await ensurePushSubscription({
                      accountId: accountDetails?.id,
                      accountType: type,
                      userId: currentUserInfo?.username || accountDetails?.id,
                      userInitiated: true,
                    });
                  } catch (error) {
                    console.error('Error enabling push notifications', error);
                  } finally {
                    closeMenu();
                  }
                }}
                sx={{
                  display: 'block',
                  textTransform: 'none',
                  textDecoration: 'none',
                  color: '#000',
                  fontWeight: 'normal',
                  padding: 0,
                  textAlign: 'left',
                  minWidth: 0,
                }}
              >
                <ListItemText
                  primary="Enable Notifications"
                  primaryTypographyProps={{
                    sx: { fontSize: '22px', lineHeight: '1.1' },
                  }}
                />
              </Button>
            </ListItem>
          )} */}
          <ListItem
            sx={{
              p: 0,
            }}
          >
            <Button
              onClick={handleLogout}
              sx={{
                display: 'block',
                textTransform: 'none',
                textDecoration: 'none',
                color: '#000',
                fontWeight: 'normal',
                padding: 0,
                textAlign: 'left',
                minWidth: 0,
              }}
            >
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  sx: { fontSize: '22px', lineHeight: '1.1' },
                }}
              />
            </Button>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default MobileMenu;
